import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import DisplaySettingsOutlinedIcon from '@mui/icons-material/DisplaySettingsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined';
import type { MeasurementPreferences } from '@kangaroo/shared';
import { colors, InterfaceSettingsPanel } from '@kangaroo/ui';

import { UserAvatarUpload } from '../../components/settings/UserAvatarUpload';
import { EmailPreferences } from '../../components/settings/EmailPreferences';
import { changePassword, deleteCurrentUserAccount, updateCurrentUser, uploadUserAvatar } from '../../features/auth';
import {
  distanceUnitLabel,
  getMeasurementPreferences,
  measurementDefaultsForLocale,
  speedUnitLabel,
  temperatureUnitLabel,
} from '../../lib/measurements';
import { resetDismissedCircuitDiscoveries } from '../../lib/circuitDiscoveries';
import { useAuthStore } from '../../store/authStore';
import { RolesPermissionsSettings } from './RolesPermissionsSettings';
import { ConnectionsSettings } from './ConnectionsSettings';

const panelSx = {
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.2,
  boxShadow: '0 0 0 1px rgba(255,255,255,0.02)',
};

const fieldSx = {
  '& .MuiInputBase-root': { minHeight: 38, fontSize: '0.76rem', borderRadius: 0.85 },
  '& .MuiInputLabel-root': { fontSize: '0.76rem' },
};

const SETTINGS_SECTIONS = [
  { id: 'profile', label: 'Profile', icon: PersonOutlineOutlinedIcon },
  { id: 'connections', label: 'Connections', icon: LinkOutlinedIcon },
  { id: 'interface', label: 'Interface', icon: DisplaySettingsOutlinedIcon },
  { id: 'measurements', label: 'Measurements', icon: StraightenOutlinedIcon },
  { id: 'roles-permissions', label: 'Roles & permissions', icon: AdminPanelSettingsOutlinedIcon },
  { id: 'password', label: 'Password', icon: LockOutlinedIcon },
  { id: 'session', label: 'Session', icon: LogoutOutlinedIcon },
] as const;

export type SettingsSection = (typeof SETTINGS_SECTIONS)[number]['id'] | 'billing';

export function parseSettingsSection(value: string | null | undefined): SettingsSection {
  const active = (value as SettingsSection | null) ?? 'profile';
  if (active === 'billing') return active;
  return SETTINGS_SECTIONS.some((section) => section.id === active) ? active : 'profile';
}

function getErrorMessage(error: unknown) {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message ?? 'Request failed';
  }

  return error instanceof Error ? error.message : 'Unknown error';
}

function getInitials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || 'U';
}

function formatRole(role: string) {
  return role
    .toLowerCase()
    .split('_')
    .map((part) => part[0]!.toUpperCase() + part.slice(1))
    .join(' ');
}

function UnitPicker<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) {
  return (
    <Box>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', mb: 0.75 }}>{label}</Typography>
      <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
        {options.map((option) => {
          const active = option.value === value;
          return (
            <Chip
              key={option.value}
              label={option.label}
              clickable
              onClick={() => onChange(option.value)}
              sx={{
                height: 30,
                fontSize: '0.72rem',
                fontWeight: 700,
                borderRadius: 0.85,
                border: '1px solid',
                borderColor: active ? 'rgba(124,58,237,0.45)' : 'divider',
                bgcolor: active ? 'rgba(124,58,237,0.14)' : 'rgba(255,255,255,0.02)',
                color: active ? colors.primary[200] : 'text.secondary',
              }}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

interface SettingsContentProps {
  section: SettingsSection;
  onSectionChange: (section: SettingsSection) => void;
  embedded?: boolean;
}

export function SettingsContent({ section, onSectionChange, embedded = false }: SettingsContentProps) {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const updateUser = useAuthStore((state) => state.updateUser);
  const clearSession = useAuthStore((state) => state.clearSession);

  const safeSection = parseSettingsSection(section);

  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  const [measurementError, setMeasurementError] = useState<string | null>(null);
  const [measurementSuccess, setMeasurementSuccess] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [sessionSuccess, setSessionSuccess] = useState<string | null>(null);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isSavingMeasurements, setIsSavingMeasurements] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: user?.name ?? '' });
  const [measurementForm, setMeasurementForm] = useState<MeasurementPreferences>(getMeasurementPreferences(user?.measurementPreferences));
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  useEffect(() => {
    if (!user) {
      return;
    }

    setProfileForm({ name: user.name });
    setMeasurementForm(getMeasurementPreferences(user.measurementPreferences));
  }, [user]);

  const initials = useMemo(() => getInitials(user?.name ?? ''), [user?.name]);

  if (safeSection === 'billing') return <Navigate to="/billing" replace />;
  if (!user) {
    return null;
  }

  async function handleAvatarUpload(file: File) {
    setIsUploadingAvatar(true);
    setProfileError(null);
    setProfileSuccess(null);

    try {
      const updatedUser = await uploadUserAvatar(file);
      updateUser(updatedUser);
      setProfileSuccess('Profile photo updated');
    } catch (error) {
      setProfileError(getErrorMessage(error));
    } finally {
      setIsUploadingAvatar(false);
    }
  }

  async function handleProfileSubmit(event: React.FormEvent) {
    event.preventDefault();
    setIsSavingProfile(true);
    setProfileError(null);
    setProfileSuccess(null);

    try {
      const updatedUser = await updateCurrentUser({ name: profileForm.name.trim() });
      updateUser(updatedUser);
      setProfileSuccess('Profile updated');
    } catch (error) {
      setProfileError(getErrorMessage(error));
    } finally {
      setIsSavingProfile(false);
    }
  }

  async function handleMeasurementSubmit(event: React.FormEvent) {
    event.preventDefault();
    setIsSavingMeasurements(true);
    setMeasurementError(null);
    setMeasurementSuccess(null);

    try {
      const updatedUser = await updateCurrentUser({ measurementPreferences: measurementForm });
      updateUser(updatedUser);
      setMeasurementSuccess('Measurement preferences updated');
    } catch (error) {
      setMeasurementError(getErrorMessage(error));
    } finally {
      setIsSavingMeasurements(false);
    }
  }

  async function handlePasswordSubmit(event: React.FormEvent) {
    event.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New password and confirmation must match');
      return;
    }

    setIsSavingPassword(true);
    setPasswordError(null);
    setPasswordSuccess(null);

    try {
      await changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordSuccess('Password changed successfully in Paddiq');
    } catch (error) {
      setPasswordError(getErrorMessage(error));
    } finally {
      setIsSavingPassword(false);
    }
  }

  function handleLogout() {
    void clearSession().then(() => navigate('/login'));
  }

  async function handleDeleteAccount() {
    setIsDeletingAccount(true);
    setDeleteError(null);

    try {
      await deleteCurrentUserAccount();
      await clearSession();
      navigate('/login', { replace: true });
    } catch (error) {
      setDeleteError(getErrorMessage(error));
      setIsDeletingAccount(false);
    }
  }

  function handleResetHiddenCircuitSuggestions() {
    resetDismissedCircuitDiscoveries(user?.id ?? null);
    setSessionSuccess('Hidden circuit suggestions reset');
  }

  return (
    <Stack spacing={1.5} sx={{ maxWidth: embedded ? undefined : 980, mx: embedded ? undefined : 'auto', pb: embedded ? 0 : 2 }}>
      {!embedded ? (
        <Box sx={{ ...panelSx, p: 1.5 }}>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 900, lineHeight: 1.2 }}>Account settings</Typography>
          <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary', mt: 0.45, lineHeight: 1.45 }}>
            Profile, measurement units, roles & permissions, password, and session controls for {user.email}.
          </Typography>
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', mt: 1.2 }}>
            <Chip label={formatRole(user.role)} size="small" sx={{ height: 24, fontSize: '0.66rem', fontWeight: 800, bgcolor: 'rgba(124,58,237,0.14)', color: colors.primary[200] }} />
            <Chip
              label={`${speedUnitLabel(measurementForm)} · ${temperatureUnitLabel(measurementForm)} · ${distanceUnitLabel(measurementForm)}`}
              size="small"
              sx={{ height: 24, fontSize: '0.66rem', fontWeight: 700, bgcolor: 'rgba(255,255,255,0.03)' }}
            />
          </Stack>
        </Box>
      ) : null}

      <Box>
        <Tabs
          value={safeSection}
          onChange={(_event, value: SettingsSection) => onSectionChange(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 36,
            mb: 1.25,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 36,
              px: 1.15,
              fontSize: '0.72rem',
              textTransform: 'none',
              fontWeight: 600,
              gap: 0.5,
            },
            '& .Mui-selected': {
              fontWeight: 800,
              color: `${colors.primary[200]} !important`,
            },
            '& .MuiTabs-indicator': {
              bgcolor: colors.primary[500],
            },
          }}
        >
          {SETTINGS_SECTIONS.map(({ id, label, icon: Icon }) => (
            <Tab
              key={id}
              value={id}
              label={label}
              icon={<Icon sx={{ fontSize: 15 }} />}
              iconPosition="start"
            />
          ))}
        </Tabs>

        <Stack spacing={1.25}>
          {safeSection === 'profile' ? <EmailPreferences key={user?.id} /> : null}
          {safeSection === 'interface' ? <InterfaceSettingsPanel /> : null}
          {safeSection === 'connections' ? <ConnectionsSettings /> : null}

          {safeSection === 'profile' ? (
            <Box sx={{ ...panelSx, p: 1.35 }}>
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 900, mb: 1.1 }}>Profile</Typography>
              <Stack spacing={1.25} component="form" onSubmit={handleProfileSubmit}>
                {profileError ? <Alert severity="error">{profileError}</Alert> : null}
                {profileSuccess ? <Alert severity="success">{profileSuccess}</Alert> : null}
                <UserAvatarUpload
                  name={user.name}
                  initials={initials}
                  avatarUrl={user.avatarUrl}
                  uploading={isUploadingAvatar}
                  onPickFile={handleAvatarUpload}
                />
                <Divider />
                <TextField
                  label="Full name"
                  value={profileForm.name}
                  onChange={(event) => setProfileForm({ name: event.target.value })}
                  fullWidth
                  sx={fieldSx}
                />
                <TextField label="Email" value={user.email} disabled fullWidth sx={fieldSx} />
                <Button type="submit" variant="contained" disabled={isSavingProfile || isUploadingAvatar} sx={{ alignSelf: 'flex-start', textTransform: 'none', fontSize: '0.76rem', fontWeight: 700 }}>
                  {isSavingProfile ? 'Saving...' : 'Save profile'}
                </Button>
              </Stack>
            </Box>
          ) : null}

          {safeSection === 'measurements' ? (
            <Box sx={{ ...panelSx, p: 1.35 }}>
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 900, mb: 0.45 }}>Measurements</Typography>
              <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary', mb: 1.2, lineHeight: 1.45 }}>
                Choose how speed, temperature, and distance appear across telemetry, vehicles, and reports.
              </Typography>
              <Stack spacing={1.5} component="form" onSubmit={handleMeasurementSubmit}>
                {measurementError ? <Alert severity="error">{measurementError}</Alert> : null}
                {measurementSuccess ? <Alert severity="success">{measurementSuccess}</Alert> : null}
                {!user.measurementPreferences ? (
                  <Alert severity="info" sx={{ fontSize: '0.74rem' }}>
                    Defaults follow your browser locale until you save a preference here.
                  </Alert>
                ) : null}
                <UnitPicker
                  label="Speed"
                  value={measurementForm.speedUnit}
                  options={[
                    { value: 'KPH', label: 'km/h' },
                    { value: 'MPH', label: 'mph' },
                  ]}
                  onChange={(speedUnit) => setMeasurementForm((current) => ({ ...current, speedUnit }))}
                />
                <UnitPicker
                  label="Temperature"
                  value={measurementForm.temperatureUnit}
                  options={[
                    { value: 'C', label: 'Celsius' },
                    { value: 'F', label: 'Fahrenheit' },
                  ]}
                  onChange={(temperatureUnit) => setMeasurementForm((current) => ({ ...current, temperatureUnit }))}
                />
                <UnitPicker
                  label="Distance"
                  value={measurementForm.distanceUnit}
                  options={[
                    { value: 'KM', label: 'Kilometers' },
                    { value: 'MI', label: 'Miles' },
                  ]}
                  onChange={(distanceUnit) => setMeasurementForm((current) => ({ ...current, distanceUnit }))}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button type="submit" variant="contained" disabled={isSavingMeasurements} sx={{ textTransform: 'none', fontSize: '0.76rem', fontWeight: 700 }}>
                    {isSavingMeasurements ? 'Saving...' : 'Save measurements'}
                  </Button>
                  <Button type="button" variant="outlined" onClick={() => setMeasurementForm(measurementDefaultsForLocale())} disabled={isSavingMeasurements} sx={{ textTransform: 'none', fontSize: '0.76rem', fontWeight: 700 }}>
                    Use browser defaults
                  </Button>
                </Stack>
              </Stack>
            </Box>
          ) : null}

          {safeSection === 'roles-permissions' ? (
            <RolesPermissionsSettings />
          ) : null}

          {safeSection === 'password' ? (
            <Box sx={{ ...panelSx, p: 1.35 }}>
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 900, mb: 0.45 }}>Change password</Typography>
              <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary', mb: 1.2, lineHeight: 1.45 }}>
                Update the password you use to sign in on web and mobile.
              </Typography>
              <Stack spacing={1.25} component="form" onSubmit={handlePasswordSubmit}>
                {passwordError ? <Alert severity="error">{passwordError}</Alert> : null}
                {passwordSuccess ? <Alert severity="success">{passwordSuccess}</Alert> : null}
                <TextField
                  label="Current password"
                  type="password"
                  name="currentPassword"
                  autoComplete="current-password"
                  required
                  value={passwordForm.currentPassword}
                  onChange={(event) => setPasswordForm((current) => ({ ...current, currentPassword: event.target.value }))}
                  fullWidth
                  sx={fieldSx}
                />
                <TextField
                  label="New password"
                  type="password"
                  name="newPassword"
                  autoComplete="new-password"
                  required
                  helperText="At least 8 characters"
                  value={passwordForm.newPassword}
                  onChange={(event) => setPasswordForm((current) => ({ ...current, newPassword: event.target.value }))}
                  fullWidth
                  sx={fieldSx}
                />
                <TextField
                  label="Confirm new password"
                  type="password"
                  name="confirmPassword"
                  autoComplete="new-password"
                  required
                  value={passwordForm.confirmPassword}
                  onChange={(event) => setPasswordForm((current) => ({ ...current, confirmPassword: event.target.value }))}
                  fullWidth
                  sx={fieldSx}
                />
                <Button type="submit" variant="contained" disabled={isSavingPassword} sx={{ alignSelf: 'flex-start', textTransform: 'none', fontSize: '0.76rem', fontWeight: 700 }}>
                  {isSavingPassword ? 'Updating...' : 'Update password'}
                </Button>
              </Stack>
            </Box>
          ) : null}

          {safeSection === 'session' ? (
            <Box sx={{ ...panelSx, p: 1.35 }}>
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 900, mb: 0.45 }}>Session</Typography>
              <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary', mb: 1.2, lineHeight: 1.45 }}>
                Sign out on this device. You can sign back in anytime with your email and password.
              </Typography>
              <Stack spacing={1.25}>
                {sessionSuccess ? <Alert severity="success" onClose={() => setSessionSuccess(null)}>{sessionSuccess}</Alert> : null}
                <Box>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, mb: 0.35 }}>Circuit suggestions</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 0.9, lineHeight: 1.45 }}>
                    Bring back unmatched circuit suggestions you previously dismissed on this device.
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<RestartAltOutlinedIcon sx={{ fontSize: 16 }} />}
                    onClick={handleResetHiddenCircuitSuggestions}
                    sx={{ textTransform: 'none', fontSize: '0.76rem', fontWeight: 700 }}
                  >
                    Reset hidden suggestions
                  </Button>
                </Box>
                <Divider />
                <Box>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, mb: 0.35 }}>Sign out</Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<LogoutOutlinedIcon sx={{ fontSize: 16 }} />}
                    onClick={handleLogout}
                    sx={{ textTransform: 'none', fontSize: '0.76rem', fontWeight: 700 }}
                  >
                    Log out
                  </Button>
                </Box>
                <Divider />
                <Box>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, mb: 0.35, color: 'error.light' }}>Delete account</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 0.9, lineHeight: 1.45 }}>
                    Permanently delete your account and remove your personal profile details from Paddiq.
                  </Typography>
                  {deleteError ? <Alert severity="error" sx={{ mb: 1 }}>{deleteError}</Alert> : null}
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteForeverOutlinedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={isDeletingAccount}
                    sx={{ textTransform: 'none', fontSize: '0.76rem', fontWeight: 700 }}
                  >
                    Delete account
                  </Button>
                </Box>
              </Stack>
            </Box>
          ) : null}
        </Stack>
      </Box>
      <Dialog open={deleteDialogOpen} onClose={() => !isDeletingAccount && setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete account?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.86rem', color: 'text.secondary', lineHeight: 1.55 }}>
            This permanently deletes your Paddiq account and signs you out. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeletingAccount} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={() => void handleDeleteAccount()} disabled={isDeletingAccount} sx={{ textTransform: 'none' }}>
            {isDeletingAccount ? 'Deleting...' : 'Delete account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSection = parseSettingsSection(searchParams.get('section'));

  return (
    <SettingsContent
      section={activeSection}
      onSectionChange={(section) => setSearchParams({ section }, { replace: true })}
    />
  );
}
